import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect, useHistory } from "react-router-dom";
import {
  FaBell,
  FaCalendarAlt,
  FaHome,
  FaPaperPlane,
  FaSyncAlt,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import BackButton from "../ChildComponent/BackButton";
import SessionManager from "../Utils/SessionManager";
import MdlSelectSession from "../dialog/MdlSelectSession";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/Notification.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  '"Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif';

const PAGE_SIZE = 40;

//Created By Cursor
function formatWhen(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

//Created By Cursor
export default function AdminNotification() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  useEffect(() => {
    try {
      const current = SessionManager.getCurrentSession()?.[0];
      if (current?.session_id) {
        setSessionId(current.session_id);
        setSessionName(current.session_name || "");
      }
    } catch (e) {
      // ignore
    }
    setSessionReady(true);
  }, []);

  const loadNotifications = useCallback(
    async ({ soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !sessionId) {
        setItems([]);
        setLoading(false);
        return;
      }
      if (soft) setRefreshing(true);
      else setLoading(true);
      try {
        const response = await axios
          .get(`${API_BASE()}getAllNotifications.php`, {
            params: {
              school_code: schoolCode,
              session_id: sessionId,
              limit: PAGE_SIZE,
              offset: 0,
            },
          })
          .then((res) => res.data);

        if (response?.api_status === "OK") {
          setItems(
            Array.isArray(response.notification) ? response.notification : []
          );
        } else {
          setItems([]);
          const remark = String(response?.api_remark || "");
          if (!/no record/i.test(remark)) {
            myToast.notify_warning(remark || "Could not load notifications.");
          }
        }
      } catch (error) {
        setItems([]);
        myToast.notify_warning("Failed to load notifications.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loggedIn || !sessionReady) return;
    if (!sessionId) {
      setLoading(false);
      return;
    }
    loadNotifications();
  }, [loggedIn, sessionReady, sessionId, loadNotifications]);

  const onSessionPicked = useCallback((id, name) => {
    setSessionId(id || "");
    setSessionName(name || "");
    setSessionDialogOpen(false);
    setItems([]);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const hay = [
        item.notification_title,
        item.notification,
        item.department,
        item.recipient_id,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [items, search]);

  const busy = loading || refreshing;

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page nt-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Admin Notification</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell nt-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBell />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Admin Notification</h1>
              <p className="sm-hero__sub">
                {sessionName
                  ? `${sessionName} · ${filtered.length} message${
                      filtered.length === 1 ? "" : "s"
                    }`
                  : "School alerts and messages to students & parents"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search notifications…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search notifications"
              />
            </div>
            <button
              type="button"
              className="nt-session"
              onClick={() => setSessionDialogOpen(true)}
              disabled={busy}
              title="Change session"
            >
              <FaCalendarAlt aria-hidden />
              <span>
                <small>Session</small>
                <strong>{sessionName || "Select"}</strong>
              </span>
            </button>
            <Link
              to="/AdminMain/SendNotification"
              className="nt-hero-btn nt-hero-btn--solid"
            >
              <FaPaperPlane aria-hidden /> Send
            </Link>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadNotifications({ soft: true })}
              disabled={busy || !sessionId}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="nt-body">
          {loading && !filtered.length ? (
            <div className="nt-loading">
              <MyLoader />
            </div>
          ) : null}

          {!loading && !sessionId ? (
            <div className="nt-empty">
              <p className="nt-empty__title">Select a session</p>
              <p className="nt-empty__hint">
                Choose a session to load notifications.
              </p>
              <button
                type="button"
                className="nt-btn nt-btn--primary"
                onClick={() => setSessionDialogOpen(true)}
              >
                <FaCalendarAlt aria-hidden /> Select session
              </button>
            </div>
          ) : null}

          {!loading && sessionId && !filtered.length ? (
            <div className="nt-empty">
              <div className="nt-empty__mark" aria-hidden>
                <FaBell />
              </div>
              <p className="nt-empty__title">
                {search.trim()
                  ? "No matching notifications"
                  : "No notifications yet"}
              </p>
              <p className="nt-empty__hint">
                {search.trim()
                  ? "Try a different search term."
                  : "Sent messages and school alerts will appear here."}
              </p>
              {!search.trim() ? (
                <Link
                  to="/AdminMain/SendNotification"
                  className="nt-btn nt-btn--primary"
                >
                  <FaPaperPlane aria-hidden /> Send message
                </Link>
              ) : null}
            </div>
          ) : null}

          {filtered.length ? (
            <div className="nt-list">
              {filtered.map((item, index) => (
                <article
                  key={item.notification_id || `${item.date_added}-${index}`}
                  className="nt-card"
                  onClick={() => setDetailItem(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDetailItem(item);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="nt-card__icon" aria-hidden>
                    <FaBell />
                  </div>
                  <div className="nt-card__body">
                    <div className="nt-card__head">
                      <h2>{item.notification_title || "Notification"}</h2>
                      <time>{formatWhen(item.date_added)}</time>
                    </div>
                    <p>{item.notification || "—"}</p>
                    <div className="nt-card__meta">
                      {item.department ? (
                        <span>Regards: {item.department}</span>
                      ) : null}
                      {item.recipient_id ? (
                        <span>Recipient: {item.recipient_id}</span>
                      ) : null}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {detailItem ? (
        <div
          className="nt-modal-backdrop"
          role="presentation"
          onClick={() => setDetailItem(null)}
        >
          <div
            className="nt-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="an-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="nt-modal__head">
              <h2 id="an-detail-title">
                {detailItem.notification_title || "Notification"}
              </h2>
              <button
                type="button"
                className="nt-modal__close"
                onClick={() => setDetailItem(null)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="nt-modal__body">
              <p className="nt-modal__when">
                {formatWhen(detailItem.date_added)}
              </p>
              <p className="nt-modal__text">
                {detailItem.notification || "—"}
              </p>
              <div className="nt-card__meta">
                {detailItem.department ? (
                  <span>Regards: {detailItem.department}</span>
                ) : null}
                {detailItem.recipient_id ? (
                  <span>Recipient: {detailItem.recipient_id}</span>
                ) : null}
              </div>
            </div>
            <footer className="nt-modal__foot">
              <button
                type="button"
                className="nt-btn nt-btn--ghost"
                onClick={() => setDetailItem(null)}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {sessionDialogOpen ? (
        <MdlSelectSession
          action={() => setSessionDialogOpen(false)}
          action2={onSessionPicked}
        />
      ) : null}
    </div>
  );
}
